import React, { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useTranslation } from 'react-i18next';

import { useAppContext } from '../AppContext';

export const CosmicAlignmentReportModal = ({ report, onClose }) => {
    const { t } = useTranslation();
    if (!report) return null;
    return (
        <div className="modal-overlay">
            <div className="bg-gray-900/80 backdrop-blur-md p-6 rounded-lg shadow-glow max-w-lg w-full text-white relative border border-cyan-500/50">
                <h3 className="text-2xl font-bold mb-4 text-cyan-300 font-playfair text-center">{t('cosmicAlignmentReportModal.title')}</h3>
                <blockquote className="border-l-4 border-cyan-400 pl-4 text-gray-200 italic mb-6 text-center">
                    "{report.summary}"
                </blockquote>
                <div className="space-y-4">
                    <div>
                        <h4 className="font-bold text-green-400 mb-2 flex items-center gap-2"><LucideIcons.Sparkles size={18} /> {t('cosmicAlignmentReportModal.harmoniousThemes')}</h4>
                        <div className="flex flex-wrap gap-2">
                            {report.themes.map((theme, i) => <span key={i} className="bg-green-900/50 text-green-300 text-sm font-semibold px-3 py-1 rounded-full">{theme}</span>)}
                        </div>
                    </div>
                    <div>
                        <h4 className="font-bold text-yellow-400 mb-2 flex items-center gap-2"><LucideIcons.Users size={18} /> {t('cosmicAlignmentReportModal.potentialPairings')}</h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-200 pl-2">
                            {report.pairings.map((pairing, i) => <li key={i}>{pairing}</li>)}
                        </ul>
                    </div>
                </div>
                <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-gray-700/50 hover:bg-gray-600/50 transition duration-300" aria-label={t('cosmicAlignmentReportModal.closeAriaLabel')}><LucideIcons.X size={20} /></button>
            </div>
        </div>
    );
};

export const StarlightSurgeModal = ({ targetProfile, onClose }) => {
    const { appFunctions, setMessage, showConfirmation } = useAppContext();
    const { t } = useTranslation();
    const [amount, setAmount] = useState(25);
    const [message, setMessageContent] = useState('');
    const [isSending, setIsSending] = useState(false);

    const handleSend = () => {
        if (amount < 25) {
            setMessage(t('starlightSurgeModal.minAmountError'));
            return;
        }
        showConfirmation({
            message: t('starlightSurgeModal.confirmationMessage', { amount, displayName: targetProfile.displayName }),
            onConfirm: async () => {
                setIsSending(true);
                try {
                    const starlightSurge = httpsCallable(appFunctions, 'starlightSurge');
                    await starlightSurge({ targetUserId: targetProfile.id, amount, message });
                    setMessage(t('starlightSurgeModal.successMessage'));
                    onClose();
                } catch (error) {
                    setMessage(t('starlightSurgeModal.errorMessage', { message: error.message }));
                } finally {
                    setIsSending(false);
                }
            }
        });
    };

    return (
        <div className="modal-overlay">
            <div className="bg-gray-900/80 backdrop-blur-md p-6 rounded-lg shadow-glow max-w-sm w-full text-white relative border border-yellow-500/50">
                <h3 className="text-xl font-bold mb-4 text-yellow-300 font-playfair">{t('starlightSurgeModal.title')}</h3>
                <p className="text-gray-300 mb-4">{t('starlightSurgeModal.description', { displayName: targetProfile.displayName })}</p>
                <input type="number" value={amount} onChange={e => setAmount(Number(e.target.value))} min="25" className="w-full bg-gray-800 p-2 rounded-lg border border-gray-700 text-center text-2xl font-bold" />
                <textarea value={message} onChange={e => setMessageContent(e.target.value)} maxLength="100" placeholder={t('starlightSurgeModal.messagePlaceholder')} className="w-full mt-3 bg-gray-800 p-2 rounded-lg border border-gray-700 h-20 resize-none"></textarea>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="small-action-button bg-gray-600">{t('starlightSurgeModal.cancel')}</button>
                    <button onClick={handleSend} disabled={isSending} className="small-action-button bg-yellow-500 text-black">{isSending ? t('starlightSurgeModal.sending') : t('starlightSurgeModal.sendButton')}</button>
                </div>
            </div>
        </div>
    );
};

export const ChartCourseModal = ({ targetProfile, onClose }) => {
    const { appFunctions, setMessage } = useAppContext();
    const { t } = useTranslation();
    const [courseName, setCourseName] = useState('');
    const [isCharting, setIsCharting] = useState(false);

    const handleChart = async () => {
        if (!courseName.trim()) {
            setMessage(t('chartCourseModal.nameRequiredError'));
            return;
        }
        setIsCharting(true);
        try {
            const chartConstellationCourse = httpsCallable(appFunctions, 'chartConstellationCourse');
            await chartConstellationCourse({ targetUserId: targetProfile.id, courseName });
            setMessage(t('chartCourseModal.successMessage', { displayName: targetProfile.displayName }));
            onClose();
        } catch (error) {
            setMessage(t('chartCourseModal.errorMessage', { message: error.message }));
        } finally {
            setIsCharting(false);
        }
    };

    return (
        <div className="modal-overlay">
            <div className="bg-gray-900/80 backdrop-blur-md p-6 rounded-lg shadow-glow max-w-sm w-full text-white relative border border-indigo-500/50">
                <h3 className="text-xl font-bold mb-4 text-indigo-300 font-playfair">{t('chartCourseModal.title')}</h3>
                <p className="text-gray-300 mb-4">{t('chartCourseModal.description', { displayName: targetProfile.displayName })}</p>
                <input type="text" value={courseName} onChange={e => setCourseName(e.target.value)} maxLength="50" placeholder={t('chartCourseModal.courseNamePlaceholder')} className="w-full bg-gray-800 p-2 rounded-lg border border-gray-700" />
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="small-action-button bg-gray-600">{t('chartCourseModal.cancel')}</button>
                    <button onClick={handleChart} disabled={isCharting} className="small-action-button bg-indigo-500 hover:bg-indigo-600">{isCharting ? t('chartCourseModal.charting') : t('chartCourseModal.beginButton')}</button>
                </div>
            </div>
        </div>
    );
};
